/**
 * Component implementing one line of the TaskList table.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2024-2026
 */
import { getJobInfos as getJobInfo } from '@/api/jobs'
import { getDatesDiffAsDays } from '@/components/jobui/utils/functions'
import { ModuleConfig } from '@/components/ModuleConfig/ModuleConfig'
import {
  getModuleByServiceName,
  getModuleDisplayName,
} from '@/components/ModuleConfig/ModuleConfigList'
import StatusComp from '@/components/StatusComp'
import { logger, LogTag } from '@/config/logger'
import JobError from '@/pages/TaskList/JobError'
import { useState } from 'react'
import { BiCoin, BiDownload } from 'react-icons/bi'
import AimdTimeAnalysisButton from './AimdTimeAnalysisButton'
import ProgressIconButton from './ProgressIconButton'

/** One time-analysis entry extracted from a job's report. */
interface TimeAnalysis {
  timeAnalysisIasId: string
  inputFileName: string
  isAi: boolean
  data?: any
}

/** Cached job details held in local state to avoid re-fetching. */
interface JobResult {
  jobId: string
  report: any
  fileList: any[]
  timeAnalysisId: any
  timeAnalysisList: TimeAnalysis[] | undefined
}

/** Props for {@link TaskListRow}. */
interface TaskListRowProps {
  job: any
}

const TaskListRow = (props: TaskListRowProps) => {
  const startDateTime = new Date(props.job?.job_start_datetime ?? 0)
  const uploadedFileName =
    props.job?.file_paths?.[0] !== undefined
      ? props.job.file_paths[0].split('/').pop()
      : undefined

  const [isLoading, setLoading] = useState(false)
  const [jobResult, setJobResult] = useState<JobResult | undefined>(undefined)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
  const module: ModuleConfig | undefined = getModuleByServiceName(
    props.job?.service_name
  )
  const moduleDisplayName = getModuleDisplayName(props.job?.service_name)

  /** Fetches the full job report and builds the time-analysis list. */
  async function getJobResult() {
    const data = await getJobInfo({ jobId: props.job?.job_id })

    const report = data?.report_info?.report
    logger.debug(LogTag.TaskList, 'report:', report)

    const fileList = data?.file_paths ?? []
    logger.debug(LogTag.TaskList, 'filePaths:', fileList)

    // Get list of Time Analysis
    const timeAnalysisList: TimeAnalysis[] = []

    if (report?.resultList) {
      for (let index = 0; index < report.resultList.length; ++index) {
        const currentResult = report.resultList.at(index)
        if (currentResult.timeAnalysis) {
          const item = {
            timeAnalysisIasId: currentResult.timeAnalysis.id,
            inputFileName: currentResult.inputFilename,
            isAi: currentResult.isAi,
          }
          timeAnalysisList.push(item)
        }
      }
    }

    const result = {
      jobId: props.job?.job_id,
      report: report,
      fileList: fileList,
      timeAnalysisId: report?.resultList?.at(0)?.timeAnalysis,
      timeAnalysisList: timeAnalysisList,
    }
    setJobResult(result)

    return result
  }

  /**
   * Handler of the click on a Download link.
   */
  const onDownloadClick = async () => {
    if (!isLoading) {
      setLoading(true)

      try {
        const result = jobResult ?? (await getJobResult())
        logger.debug('Job result:', result)
        if (result !== undefined) {
          if (module) {
            await module.downloadResult(result)
          } else {
            await ModuleConfig.defaultDownloadResult(result)
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          setErrorMsg(error.message)
        } else {
          setErrorMsg(String(error)) // fallback for non-Error values
        }
      }
      setLoading(false)
    }
  }

  return (
    <>
      <tr className="job-info" data-process-job-id={props.job?.job_id}>
        <td>{startDateTime.toLocaleString()}</td>
        <td className="truncate">
          {props.job?.job_input_args?.job_label ??
            uploadedFileName ??
            props.job?.job_id}
        </td>
        <td>{moduleDisplayName}</td>
        <td>
          <StatusComp status={props.job.job_status} />
        </td>
        <td>
          {/* Display job cost if available */}
          {props.job.job_status !== 'error' && (
            <div>
              {props.job.job_cost !== undefined && (
                <>
                  <BiCoin />
                  {Math.round(Number(props.job.job_cost * 100)) / 100}
                </>
              )}
            </div>
          )}
        </td>
        <td className="resultCol">
          {!errorMsg &&
            props.job?.job_status !== 'error' &&
            props.job?.job_status !== 'running' &&
            props.job?.job_status !== 'created' && (
              <>
                {getDatesDiffAsDays(props.job?.job_end_datetime) >= 7 ? (
                  <div className="text-gray-300">Expired</div>
                ) : (
                  <div className="download-container">
                    <ProgressIconButton
                      onClick={onDownloadClick}
                      isWaiting={isLoading}
                      isWaitingShowlabel={true}
                      icon={BiDownload}
                      label="Download"
                    />

                    {props.job.job_input_args?.timeAnalysis == true && (
                      <AimdTimeAnalysisButton
                        jobResult={props.job}
                        label="View charts"
                      />
                    )}
                  </div>
                )}
              </>
            )}
          {(props.job?.job_status === 'error' || errorMsg) && (
            <JobError jobId={props.job?.job_id} message={errorMsg} />
          )}
        </td>
      </tr>
    </>
  )
}

export default TaskListRow
